import type { FC } from "react";
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import type { IconName } from "../NavIcon";
import type { Breadcrumb } from "@/types/view";
import {
  addBreadCrumbs,
  currentRouteSelector,
  setCurrentRoute,
} from "@/features/currentRouteSlice";
import { getUrlFromPath } from "@/routerConfig";
import { shouldHandleOwnBreadcrumbs } from "@/routerConfig/breadcrumbConfig";
import {
  resetExternalIframe,
  resumeExternalRouteSync,
  suppressExternalRouteSync,
} from "@/routerConfig/externalWindowEventSync";
import { getPathFromLocation } from "@/routerConfig/router.helpers";
import { useAppDispatch, useAppSelector } from "@/storeHooks";
import NavBtnContent from "./NavBtnContent";
import css from "./NavMenu.module.css";

type NavMenuItemProps = {
  path: string;
  icon?: IconName;
  isBeta?: boolean;
  isExternal?: boolean;
  children: string;
  breadCrumbHandledExternally?: boolean;
  parentLabel: string;
};

const isSameOrChildPath = (candidate: string, basePath: string) =>
  candidate === basePath || candidate.startsWith(`${basePath}/`);

/**
 * Leaf level nav menu item
 */
const NavMenuItem: FC<NavMenuItemProps> = ({
  children,
  path,
  isExternal,
  icon,
  isBeta,
  breadCrumbHandledExternally,
  parentLabel,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const href = getUrlFromPath(path);
  const currentRoute = useAppSelector(currentRouteSelector);
  const isCurrentRoute = currentRoute.startsWith(path);

  useEffect(() => {
    if (isCurrentRoute && breadCrumbHandledExternally !== true) {
      const breadCrumbs: Breadcrumb[] = [];
      if (parentLabel) {
        breadCrumbs.push({ label: parentLabel, path: undefined });
      }
      if (children) {
        breadCrumbs.push({ label: children, path: undefined });
      }
      if (!shouldHandleOwnBreadcrumbs(currentRoute)) {
        dispatch(addBreadCrumbs(breadCrumbs));
      }
    }
  }, [
    breadCrumbHandledExternally,
    children,
    currentRoute,
    dispatch,
    isCurrentRoute,
    parentLabel,
    path,
  ]);

  return (
    <li key={path}>
      <a
        href={href}
        aria-current={isCurrentRoute ? "page" : undefined}
        className={css.itemButton}
        onClick={(e) => {
          // Check if Command/Ctrl key is pressed
          if (e.metaKey || e.ctrlKey) {
            // Let the default behavior handle opening in new tab
            return;
          }

          // set the current route for breadcrumbs
          dispatch(setCurrentRoute(path));

          e.preventDefault();

          if (isExternal) {
            const browserPath = getPathFromLocation(window.location.pathname);
            const alreadyOnTargetInUrl = isSameOrChildPath(browserPath, path);
            // TB may have replaceState'd the bar to /app/customers while RR
            // still thinks we are on /home — navigate("/home") is then a no-op.
            const rrDesyncedOnTarget =
              isSameOrChildPath(location.pathname, path) &&
              !isSameOrChildPath(browserPath, path);

            if (alreadyOnTargetInUrl) {
              // Already on this route in the URL bar — do not full-page refresh
              return;
            }

            if (rrDesyncedOnTarget) {
              // Fix the bar and reload only the iframe (parent SPA stays mounted)
              suppressExternalRouteSync();
              window.history.replaceState(null, "", href);
              resetExternalIframe(path);
              resumeExternalRouteSync();
              return;
            }
          }

          // Ignore late TB handleRouteChange after leaving the iframe route
          suppressExternalRouteSync();
          navigate(path);
        }}
      >
        <NavBtnContent icon={icon} isBeta={isBeta}>
          {children}
        </NavBtnContent>
      </a>
    </li>
  );
};

export default NavMenuItem;
